import {
  Color,
  ConeGeometry,
  CylinderGeometry,
  Mesh,
  MeshStandardMaterial,
  Vector3,
  type BufferGeometry,
  type Scene,
} from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import {
  GRAVITY,
  MAX_FLIGHT,
  MUZZLE_SPEED,
  positionAt,
  velocityAt,
} from '../utils/ballistics';
import { WORLD_SIZE, terrainHeight } from '../world/heightfield';
import { softSprite } from '../world/textures';
import { ParticleField } from './ParticleField';

/**
 * Les obus en vol.
 *
 * Chaque obus garde son point de depart et sa vitesse initiale, et sa
 * position est *recalculee* a chaque frame par la solution fermee
 * `positionAt`. Rien n'est integre pas a pas : l'obus passe exactement la ou
 * la trajectoire predite dans la lunette annoncait qu'il passerait, a 60 comme
 * a 144 images par seconde. cf. doc/04-balistique.md
 */

/**
 * Nombre d'obus simultanes. Le rechargement dure 1,2 s et le temps de vol
 * depasse rarement 5 s : quatre ou cinq obus en l'air au maximum. Seize laisse
 * de la marge sans jamais allouer en vol.
 */
const POOL = 16;

/**
 * Longueur maximale d'un sous-pas de collision, en metres.
 *
 * A 82 m/s et 1/20 s de delta borne, un obus avance de 4,1 m entre deux
 * frames -- plus que le rayon d'une cible. Sans decoupage, il la traverserait
 * sans la toucher une frame sur deux. cf. doc/04-balistique.md
 */
const SUBSTEP_LEN = 1.2;

/** Distance entre deux particules de traceur, en metres. */
const TRACER_SPACING = 0.9;

/** Resultat d'un test de collision : indice de la cible, ou null. */
export type HitTest = (from: Vector3, to: Vector3) => number | null;

export type ImpactHandler = (
  point: Vector3,
  /** Indice de la cible touchee, ou null pour un impact au sol. */
  target: number | null,
  /** Direction d'arrivee de l'obus, normalisee. */
  incoming: Vector3,
) => void;

interface Shell {
  readonly origin: Vector3;
  readonly velocity: Vector3;
  readonly mesh: Mesh;
  t: number;
  alive: boolean;
  trailDebt: number;
}

/** Corps de l'obus : un fuseau, oriente sur +z comme le canon. */
function shellGeometry(): BufferGeometry {
  const parts = [
    new CylinderGeometry(0.055, 0.065, 0.34, 8).rotateX(Math.PI / 2),
    new ConeGeometry(0.055, 0.22, 8).rotateX(Math.PI / 2).translate(0, 0, 0.28),
    // Empennage : quatre ailettes. Invisibles en vol, mais elles donnent a
    // l'obus une silhouette correcte sur les captures au ralenti.
    ...[0, 1, 2, 3].map((i) =>
      new CylinderGeometry(0.012, 0.012, 0.16, 4)
        .rotateZ(Math.PI / 2)
        .rotateZ((i * Math.PI) / 2)
        .translate(0, 0, -0.16),
    ),
  ];
  return mergeGeometries(parts, false)!;
}

export class Shells {
  /** Nombre de coups tires depuis le debut de la partie. */
  shotsFired = 0;

  private readonly pool: Shell[] = [];
  private readonly tracers: ParticleField;

  private readonly tracerColor = new Color(0xffb257);
  private readonly tmpPrev = new Vector3();
  private readonly tmpNext = new Vector3();
  private readonly tmpVel = new Vector3();
  private readonly tmpDir = new Vector3();
  private readonly tracerPos = new Vector3();
  private readonly tracerVel = new Vector3();
  private readonly forward = new Vector3(0, 0, 1);

  constructor(scene: Scene) {
    const geo = shellGeometry();
    const mat = new MeshStandardMaterial({
      color: 0x59534a,
      roughness: 0.4,
      metalness: 0.7,
      // Le culot du traceur reste chaud pendant tout le vol.
      emissive: 0x702c05,
      emissiveIntensity: 1.1,
    });

    for (let i = 0; i < POOL; i++) {
      const mesh = new Mesh(geo, mat);
      mesh.visible = false;
      mesh.castShadow = true;
      scene.add(mesh);
      this.pool.push({
        origin: new Vector3(),
        velocity: new Vector3(),
        mesh,
        t: 0,
        alive: false,
        trailDebt: 0,
      });
    }

    this.tracers = new ParticleField(scene, {
      capacity: 900,
      texture: softSprite(),
      blend: 'additive',
      // Le traceur ne retombe pas et ne derive pas : il brule sur place.
      fadePower: 2.2,
    });
  }

  /** Met un obus en l'air. Renvoie false si le lot est sature. */
  fire(origin: Vector3, direction: Vector3): boolean {
    const shell = this.pool.find((s) => !s.alive);
    if (!shell) return false;
    shell.origin.copy(origin);
    shell.velocity.copy(direction).normalize().multiplyScalar(MUZZLE_SPEED);
    shell.t = 0;
    shell.trailDebt = 0;
    shell.alive = true;
    shell.mesh.visible = true;
    shell.mesh.position.copy(origin);
    this.shotsFired++;
    return true;
  }

  update(dt: number, hitTest: HitTest, onImpact: ImpactHandler): void {
    const half = WORLD_SIZE / 2;

    for (const shell of this.pool) {
      if (!shell.alive) continue;

      let remaining = dt;
      positionAt(shell.origin, shell.velocity, shell.t, this.tmpPrev);

      while (remaining > 1e-6) {
        // Le pas est borne en *distance* : un obus lent pres de l'apogee n'a
        // pas besoin d'etre decoupe, un obus rapide si.
        velocityAt(shell.velocity, shell.t, this.tmpVel);
        const speed = this.tmpVel.length() || MUZZLE_SPEED;
        const step = Math.min(remaining, SUBSTEP_LEN / speed);
        remaining -= step;
        shell.t += step;

        positionAt(shell.origin, shell.velocity, shell.t, this.tmpNext);

        // Cibles d'abord : un obus qui touche une cible posee au sol doit
        // compter comme un coup au but, pas comme un impact de terrain.
        const target = hitTest(this.tmpPrev, this.tmpNext);
        if (target !== null) {
          this.tmpDir.copy(this.tmpNext).sub(this.tmpPrev).normalize();
          onImpact(this.tmpNext, target, this.tmpDir);
          this.retire(shell);
          break;
        }

        if (this.tmpNext.y <= terrainHeight(this.tmpNext.x, this.tmpNext.z)) {
          this.resolveGround(shell, shell.t - step, shell.t);
          this.tmpDir.copy(this.tmpNext).sub(this.tmpPrev).normalize();
          onImpact(this.tmpNext, null, this.tmpDir);
          this.retire(shell);
          break;
        }

        this.layTracer(shell, this.tmpPrev, this.tmpNext);
        this.tmpPrev.copy(this.tmpNext);

        if (
          shell.t >= MAX_FLIGHT ||
          Math.abs(this.tmpNext.x) > half ||
          Math.abs(this.tmpNext.z) > half
        ) {
          // Hors du monde ou temps de vol epuise : l'obus disparait sans
          // impact. Rien a signaler, mais il faut liberer la place.
          this.retire(shell);
          break;
        }
      }

      if (!shell.alive) continue;

      shell.mesh.position.copy(this.tmpNext);
      // L'obus pointe dans le sens de sa vitesse : au depart presque a plat,
      // a l'arrivee piquant vers le sol.
      velocityAt(shell.velocity, shell.t, this.tmpVel).normalize();
      shell.mesh.quaternion.setFromUnitVectors(this.forward, this.tmpVel);
    }

    this.tracers.update(dt);
  }

  /**
   * Affine l'instant d'impact au sol par dichotomie, puis ecrit le point dans
   * `tmpNext`.
   *
   * Huit iterations ramenent l'erreur a 1,2 / 256 soit 5 mm : assez pour que
   * la trace et le cratere de fumee soient poses au bon endroit, sans qu'on
   * voie l'obus s'enfoncer.
   */
  private resolveGround(shell: Shell, lo: number, hi: number): void {
    let a = lo;
    let b = hi;
    for (let i = 0; i < 8; i++) {
      const mid = (a + b) * 0.5;
      positionAt(shell.origin, shell.velocity, mid, this.tmpNext);
      if (this.tmpNext.y <= terrainHeight(this.tmpNext.x, this.tmpNext.z)) b = mid;
      else a = mid;
    }
    positionAt(shell.origin, shell.velocity, b, this.tmpNext);
    shell.t = b;
  }

  /** Pose des particules de traceur a intervalle regulier le long du vol. */
  private layTracer(shell: Shell, from: Vector3, to: Vector3): void {
    const length = from.distanceTo(to);
    shell.trailDebt += length;
    while (shell.trailDebt >= TRACER_SPACING) {
      const back = shell.trailDebt - TRACER_SPACING;
      shell.trailDebt = back;
      const k = length > 1e-6 ? 1 - back / length : 1;
      this.tracerPos.lerpVectors(from, to, Math.min(1, Math.max(0, k)));
      this.tracerVel.set(0, 0, 0);
      this.tracers.spawn(this.tracerPos, this.tracerVel, 0.42, this.tracerColor, 1.5);
    }
  }

  private retire(shell: Shell): void {
    shell.alive = false;
    shell.mesh.visible = false;
  }

  get inFlight(): number {
    return this.pool.reduce((n, s) => n + (s.alive ? 1 : 0), 0);
  }

  reset(): void {
    this.shotsFired = 0;
    for (const shell of this.pool) this.retire(shell);
    this.tracers.reset();
  }

  /** Temps de vol restant du plus vieil obus, pour information. */
  get oldestFlightTime(): number {
    let t = 0;
    for (const s of this.pool) if (s.alive && s.t > t) t = s.t;
    return t;
  }

  dispose(): void {
    this.tracers.dispose();
  }
}

/** Reexporte pour les tests : la gravite employee par les obus. */
export const SHELL_GRAVITY = GRAVITY;
